import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage, useAuth, api } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { 
  Car, Wrench, ClipboardList, BarChart3, Plus, Loader2, 
  CheckCircle2, XCircle, Clock, Mail, Phone, User, Calendar,
  Edit, Trash2, MoreHorizontal, TrendingUp, Lock, Unlock
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Auto service categories
const AUTO_SERVICE_CATEGORIES = [
  'tires', 'diagnostics', 'engine', 'bodywork', 'brakes',
  'oil_change', 'transmission', 'electrical', 'ac_heating',
  'exhaust', 'suspension', 'general_repair'
];

// Status badge component
const StatusBadge = ({ status, t }) => {
  const variants = {
    pending: { variant: 'outline', className: 'border-amber-500 text-amber-600', icon: Clock },
    approved: { variant: 'outline', className: 'border-green-500 text-green-600', icon: CheckCircle2 },
    rejected: { variant: 'outline', className: 'border-red-500 text-red-600', icon: XCircle },
    cancelled: { variant: 'outline', className: 'border-gray-500 text-gray-600', icon: XCircle }
  };
  
  const config = variants[status] || variants.pending;
  const Icon = config.icon;
  
  return (
    <Badge variant={config.variant} className={config.className}>
      <Icon className="h-3 w-3 mr-1" />
      {t(`workshop.status.${status}`) || status}
    </Badge>
  );
};

// Workshop Dashboard Component
export const WorkshopDashboard = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('requests');
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [services, setServices] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Service form state
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    title: '',
    description: '',
    category: '',
    price_from: '',
    location_country: user?.country,
    location_city: ''
  });
  const [saving, setSaving] = useState(false);

  // Approve/Reject dialog state
  const [actionDialog, setActionDialog] = useState({ open: false, request: null, action: null });
  const [actionMessage, setActionMessage] = useState('');
  const [actioning, setActioning] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [requestsRes, servicesRes, analyticsRes] = await Promise.all([
        api.get('/auto/company/requests'),
        api.get('/auto/company/services'),
        api.get('/auto/company/analytics')
      ]);
      setRequests(requestsRes.data);
      setServices(servicesRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Failed to fetch workshop data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter requests by status
  const filteredRequests = requests.filter(req => 
    statusFilter === 'all' || req.status === statusFilter
  );

  // Handle approve/reject
  const handleAction = async () => {
    if (!actionDialog.request || !actionDialog.action) return;
    
    setActioning(true);
    try {
      await api.post(`/auto/requests/${actionDialog.request.id}/${actionDialog.action}`, {
        message: actionMessage
      });
      toast.success(
        actionDialog.action === 'approve' 
          ? t('workshop.requestApproved')
          : t('workshop.requestRejected')
      );
      setActionDialog({ open: false, request: null, action: null });
      setActionMessage('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setActioning(false);
    }
  };

  // Handle service create/edit
  const handleSaveService = async () => {
    if (!serviceForm.title || !serviceForm.description || !serviceForm.category || 
        !serviceForm.location_country || !serviceForm.location_city) {
      toast.error(t('common.fillRequired'));
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...serviceForm,
        price_from: serviceForm.price_from ? parseFloat(serviceForm.price_from) : null
      };

      if (editingService) {
        await api.put(`/auto/services/${editingService.id}`, data);
        toast.success(t('workshop.serviceUpdated'));
      } else {
        await api.post('/auto/services', data);
        toast.success(t('workshop.serviceCreated'));
      }
      
      setShowServiceDialog(false);
      resetServiceForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!confirm(t('workshop.confirmDelete'))) {
      return;
    }
    
    try {
      await api.delete(`/auto/services/${serviceId}`);
      toast.success(t('workshop.serviceDeleted'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const resetServiceForm = () => {
    setServiceForm({
      title: '',
      description: '',
      category: '',
      price_from: '',
      location_country: user?.country,
      location_city: ''
    });
    setEditingService(null);
  };

  const openEditService = (service) => {
    setEditingService(service);
    setServiceForm({
      title: service.title,
      description: service.description,
      category: service.category,
      price_from: service.price_from?.toString(),
      location_country: service.location_country,
      location_city: service.location_city
    });
    setShowServiceDialog(true);
  };

  if (loading) {
    return (
      <Card className="mt-8">
        <CardHeader>
          <Skeleton className="h-8 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-8" data-testid="workshop-dashboard">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Car className="h-6 w-6 text-brand" />
            <div>
              <CardTitle>{t('workshop.title')}</CardTitle>
              <CardDescription>{t('workshop.subtitle')}</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="requests" className="flex items-center gap-2" data-testid="tab-requests">
              <ClipboardList className="h-4 w-4" />
              {t('workshop.requests')}
              {requests.filter(r => r.status === 'pending').length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center rounded-full text-xs">
                  {requests.filter(r => r.status === 'pending').length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2" data-testid="tab-services">
              <Wrench className="h-4 w-4" />
              {t('workshop.services')}
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2" data-testid="tab-analytics">
              <BarChart3 className="h-4 w-4" />
              {t('workshop.analytics')}
            </TabsTrigger>
          </TabsList>

          {/* REQUESTS TAB */}
          <TabsContent value="requests">
            <div className="space-y-4">
              {/* Status filter */}
              <div className="flex items-center gap-4 mb-4">
                <Label className="text-sm">{t('common.filters')}:</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40" data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('common.all')}</SelectItem>
                    <SelectItem value="pending">{t('workshop.status.pending')}</SelectItem>
                    <SelectItem value="approved">{t('workshop.status.approved')}</SelectItem>
                    <SelectItem value="rejected">{t('workshop.status.rejected')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filteredRequests.length === 0 ? (
                <Card className="bg-muted/30">
                  <CardContent className="py-12 text-center">
                    <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-lg font-medium">{t('workshop.noRequests')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('workshop.noRequestsDesc')}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {filteredRequests.map((request) => (
                    <Card key={request.id} className="border-l-4" style={{
                      borderLeftColor: request.status === 'pending' ? '#f59e0b' : 
                                       request.status === 'approved' ? '#22c55e' : 
                                       request.status === 'rejected' ? '#ef4444' : '#6b7280'
                    }} data-testid={`request-${request.id}`}>
                      <CardContent className="py-4">
                        <div className="flex flex-col md:flex-row justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-3">
                              <StatusBadge status={request.status} t={t} />
                              <span className="text-sm text-muted-foreground">
                                {new Date(request.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            
                            <h4 className="font-medium">{request.service_title}</h4>
                            <p className="text-sm text-muted-foreground line-clamp-2">{request.message}</p>
                            
                            <div className="flex flex-wrap gap-4 text-sm">
                              {request.status === 'approved' ? (
                                // Show unlocked contact for approved requests
                                <div className="flex flex-wrap gap-4 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <User className="h-4 w-4 text-muted-foreground" />
                                    <span>{request.requester_name}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Mail className="h-4 w-4 text-muted-foreground" />
                                    <a href={`mailto:${request.requester_email}`} className="text-brand hover:underline">
                                      {request.requester_email}
                                    </a>
                                  </div>
                                  {request.requester_phone && (
                                    <div className="flex items-center gap-2">
                                      <Phone className="h-4 w-4 text-muted-foreground" />
                                      <a href={`tel:${request.requester_phone}`} className="text-brand hover:underline">
                                        {request.requester_phone}
                                      </a>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                // Show locked contact for pending/rejected
                                <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                                  <Lock className="h-4 w-4 text-amber-600" />
                                  <span className="text-sm text-muted-foreground">
                                    {request.requester_name} • {t('workshop.contactLockedUntilApprove')}
                                  </span>
                                </div>
                              )}
                              
                              {request.preferred_date && (
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4 text-muted-foreground" />
                                  <span>{new Date(request.preferred_date).toLocaleDateString()}</span>
                                </div>
                              )}
                              
                              {request.vehicle_info && (
                                <div className="flex items-center gap-2">
                                  <Car className="h-4 w-4 text-muted-foreground" />
                                  <span>{request.vehicle_info}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {request.status === 'pending' && (
                            <div className="flex md:flex-col gap-2">
                              <Button
                                size="sm"
                                onClick={() => setActionDialog({ open: true, request, action: 'approve' })}
                                data-testid={`approve-${request.id}`}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                {t('workshop.approve')}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setActionDialog({ open: true, request, action: 'reject' })}
                                data-testid={`reject-${request.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                {t('workshop.reject')}
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* SERVICES TAB */}
          <TabsContent value="services">
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button 
                  onClick={() => { resetServiceForm(); setShowServiceDialog(true); }}
                  data-testid="add-service-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('workshop.addService')}
                </Button>
              </div>

              {services.length === 0 ? (
                <Card className="bg-muted/30">
                  <CardContent className="py-12 text-center">
                    <Wrench className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-lg font-medium">{t('workshop.noServices')}</p>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t('workshop.noServicesDesc')}
                    </p>
                    <Button onClick={() => { resetServiceForm(); setShowServiceDialog(true); }}>
                      <Plus className="h-4 w-4 mr-2" />
                      {t('workshop.createFirstService')}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {services.map((service) => (
                    <Card key={service.id} data-testid={`service-${service.id}`}>
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{service.title}</CardTitle>
                            <CardDescription>
                              {t(`autoCategories.${service.category}`) || service.category}
                            </CardDescription>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditService(service)}>
                                <Edit className="h-4 w-4 mr-2" />
                                {t('common.edit')}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDeleteService(service.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                {t('common.delete')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                          {service.description}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant={service.is_active ? 'default' : 'secondary'}>
                            {service.is_active ? t('workshop.active') : t('workshop.inactive')}
                          </Badge>
                          {service.price_from && (
                            <Badge variant="outline">€{service.price_from}+</Badge>
                          )}
                          <Badge variant="outline">
                            {service.requests_count} {t('workshop.requests')}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ANALYTICS TAB */}
          <TabsContent value="analytics">
            {analytics ? (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-brand">{analytics.total_requests}</p>
                        <p className="text-sm text-muted-foreground">{t('workshop.totalRequests')}</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-green-600">{analytics.approved_requests}</p>
                        <p className="text-sm text-muted-foreground">{t('workshop.approved')}</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-red-600">{analytics.rejected_requests}</p>
                        <p className="text-sm text-muted-foreground">{t('workshop.rejected')}</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-3xl font-bold">{analytics.active_services}</p>
                        <p className="text-sm text-muted-foreground">{t('workshop.activeServices')}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Most Requested Service */}
                {analytics.most_requested_service && (
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-brand" />
                        <CardTitle className="text-lg">{t('workshop.mostRequested')}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{analytics.most_requested_service.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {t(`autoCategories.${analytics.most_requested_service.category}`) || analytics.most_requested_service.category}
                          </p>
                        </div>
                        <Badge className="bg-brand/10 text-brand">
                          {analytics.most_requested_service.request_count} {t('workshop.requests')}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Category Breakdown */}
                {Object.keys(analytics.category_breakdown || {}).length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">{t('workshop.categoryBreakdown')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries(analytics.category_breakdown).map(([category, count]) => (
                          <div key={category} className="flex items-center justify-between">
                            <span className="text-sm">{t(`autoCategories.${category}`) || category}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-brand rounded-full"
                                  style={{ width: `${(count / analytics.total_requests) * 100}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium w-8 text-right">{count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {analytics.total_requests === 0 && (
                  <Card className="bg-muted/30">
                    <CardContent className="py-12 text-center">
                      <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-lg font-medium">{t('workshop.noAnalytics')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('workshop.noAnalyticsDesc')}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Skeleton className="h-64" />
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Service Create/Edit Dialog */}
      <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingService ? t('workshop.editService') : t('workshop.addService')}
            </DialogTitle>
            <DialogDescription>
              {t('workshop.serviceFormDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('auto.serviceTitle')} *</Label>
              <Input
                value={serviceForm.title}
                onChange={(e) => setServiceForm({...serviceForm, title: e.target.value})}
                placeholder={t('auto.serviceTitlePlaceholder')}
                data-testid="service-form-title"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('auto.category')} *</Label>
              <Select 
                value={serviceForm.category} 
                onValueChange={(v) => setServiceForm({...serviceForm, category: v})}
              >
                <SelectTrigger data-testid="service-form-category">
                  <SelectValue placeholder={t('auto.selectCategory')} />
                </SelectTrigger>
                <SelectContent>
                  {AUTO_SERVICE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {t(`autoCategories.${cat}`) || cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('auto.description')} *</Label>
              <Textarea
                value={serviceForm.description}
                onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                placeholder={t('auto.descriptionPlaceholder')}
                rows={3}
                data-testid="service-form-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('auto.city')} *</Label>
                <Input
                  value={serviceForm.location_city}
                  onChange={(e) => setServiceForm({...serviceForm, location_city: e.target.value})}
                  placeholder={t('auto.cityPlaceholder')}
                  data-testid="service-form-city"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('auto.priceFrom')}</Label>
                <Input
                  type="number"
                  value={serviceForm.price_from}
                  onChange={(e) => setServiceForm({...serviceForm, price_from: e.target.value})}
                  placeholder="0.00"
                  data-testid="service-form-price"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServiceDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveService} disabled={saving} data-testid="save-service-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingService ? t('common.save') : t('workshop.createService')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve/Reject Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, request: null, action: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.action === 'approve' 
                ? t('workshop.approveRequest')
                : t('workshop.rejectRequest')}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.action === 'approve'
                ? t('workshop.approveDesc')
                : t('workshop.rejectDesc')}
            </DialogDescription>
          </DialogHeader>
          {actionDialog.request && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{actionDialog.request.service_title}</p>
                <p className="text-sm text-muted-foreground">{actionDialog.request.requester_name}</p>
              </div>
              <div className="space-y-2">
                <Label>{t('workshop.responseMessage')}</Label>
                <Textarea
                  value={actionMessage}
                  onChange={(e) => setActionMessage(e.target.value)}
                  placeholder={
                    actionDialog.action === 'approve'
                      ? t('workshop.approveMessagePlaceholder')
                      : t('workshop.rejectMessagePlaceholder')
                  }
                  rows={3}
                  data-testid="action-message"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setActionDialog({ open: false, request: null, action: null })}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleAction} 
              disabled={actioning}
              variant={actionDialog.action === 'reject' ? 'destructive' : 'default'}
              data-testid="confirm-action-btn"
            >
              {actioning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {actionDialog.action === 'approve' 
                ? t('workshop.approve')
                : t('workshop.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default WorkshopDashboard;
